
import React, { useState, useEffect } from 'react';
import { bundleService } from '../services/bundleService';
import { storage } from '../lib/storage';
import { Bundle, Product } from '../types';
import { 
  Plus, 
  Trash2, 
  Zap, 
  Package, 
  Check, 
  X, 
  Loader2, 
  ShoppingBag, 
  Percent,
  TrendingUp,
  Award,
  Sparkles
} from 'lucide-react';

interface Props {
  storeId: string;
}

const AdminBundles: React.FC<Props> = ({ storeId }) => {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    discountPercentage: 10,
    productIds: [] as string[],
    isActive: true
  });

  useEffect(() => {
    loadData();
  }, [storeId]);

  const loadData = () => {
    setIsLoading(true);
    setBundles(bundleService.getStoreBundles(storeId));
    setProducts(storage.getProducts().filter(p => p.storeId === storeId));
    setIsLoading(false);
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.productIds.length < 2) return alert("Bhai, bundle banane ke liye kam se kam 2 products toh chahiye!");
    
    bundleService.saveBundle({
      ...formData,
      id: 'bundle-' + Date.now(),
      storeId
    });
    
    setShowModal(false);
    loadData();
    setFormData({ name: '', discountPercentage: 10, productIds: [], isActive: true });
  };

  const toggleProduct = (id: string) => {
    setFormData(prev => ({
      ...prev,
      productIds: prev.productIds.includes(id) 
        ? prev.productIds.filter(p => p !== id) 
        : [...prev.productIds, id]
    }));
  };

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">Bundle Architect</h1>
          <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Multi-Item Sales Multiplier</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all flex items-center gap-2"
        >
          <Plus className="h-4 w-4" /> Build Power Set
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
         <div className="lg:col-span-2 space-y-6">
            {bundles.length === 0 ? (
              <div className="bg-white p-24 text-center space-y-4 rounded-[40px] border border-dashed border-gray-200 opacity-50">
                 <Package className="h-16 w-16 mx-auto text-gray-300" />
                 <p className="font-black uppercase tracking-widest text-xs">No active bundles. Create combos to boost your AOV!</p>
              </div>
            ) : (
              bundles.map(bundle => (
                <div key={bundle.id} className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl flex flex-col md:flex-row justify-between items-center gap-6 group hover:border-blue-200 transition-all">
                   <div className="flex items-center gap-6">
                      <div className="w-20 h-20 rounded-3xl bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">
                         <Zap className="h-10 w-10 fill-current" />
                      </div>
                      <div>
                         <div className="flex items-center gap-3">
                            <h3 className="text-xl font-black text-gray-900">{bundle.name}</h3>
                            <span className="bg-emerald-50 text-emerald-600 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest">{bundle.discountPercentage}% OFF</span>
                         </div>
                         <div className="flex flex-wrap gap-2 mt-3">
                            {bundle.productIds.map(id => {
                               const p = products.find(prod => prod.id === id);
                               return p ? (
                                 <span key={id} className="text-[9px] font-bold text-gray-400 bg-gray-50 px-2 py-1 rounded border border-gray-100">{p.name}</span>
                               ) : null;
                            })}
                         </div>
                      </div>
                   </div>
                   <div className="flex items-center gap-3">
                      <button 
                        onClick={() => { bundleService.deleteBundle(bundle.id); loadData(); }}
                        className="p-4 bg-gray-50 text-gray-400 hover:text-red-600 rounded-2xl transition-all"
                      >
                         <Trash2 className="h-5 w-5" />
                      </button>
                   </div>
                </div>
              ))
            )}
         </div>

         <div className="space-y-8">
            <div className="bg-gray-900 p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden group">
               <div className="absolute top-0 right-0 p-8 opacity-20 group-hover:scale-110 transition-transform"><TrendingUp className="h-24 w-24" /></div>
               <div className="relative space-y-6">
                  <h3 className="text-xl font-black tracking-tight">Combo Psychology</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                     Bhai, data shows users are 70% more likely to buy a "complete setup" than searching for individual accessories.
                  </p>
                  <div className="p-6 bg-white/5 rounded-3xl border border-white/10 space-y-4">
                     <div className="flex items-center gap-3">
                        <Award className="h-5 w-5 text-blue-400" />
                        <p className="text-xs font-bold uppercase tracking-widest">Increases Basket Size</p>
                     </div>
                     <div className="flex items-center gap-3">
                        <Percent className="h-5 w-5 text-emerald-400" />
                        <p className="text-xs font-bold uppercase tracking-widest">Reduces Acquisition Cost</p>
                     </div>
                  </div>
               </div>
            </div>

            <div className="bg-blue-50 p-8 rounded-[2.5rem] border border-blue-100 flex items-start gap-4">
               <Sparkles className="h-6 w-6 text-blue-600 shrink-0 mt-1" />
               <p className="text-[10px] text-blue-700 font-medium leading-relaxed uppercase tracking-wider">
                  Lynqar Rule: Manual bundles override AI suggestions on product pages.
               </p>
            </div>
         </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[200] bg-gray-950/80 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-2xl rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                 <h3 className="text-2xl font-black text-gray-900 tracking-tight">Create Power Bundle</h3>
                 <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all"><X className="h-6 w-6 text-gray-400" /></button>
              </div>
              <form onSubmit={handleCreate} className="p-10 space-y-8 max-h-[70vh] overflow-y-auto">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Bundle Name</label>
                       <input required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g. Creator Toolkit" className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Bundle Discount (%)</label>
                       <input type="number" value={formData.discountPercentage} onChange={e => setFormData({...formData, discountPercentage: Number(e.target.value)})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                 </div>

                 <div className="space-y-4">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Products (Min 2)</label>
                    <div className="grid grid-cols-1 gap-3 max-h-48 overflow-y-auto pr-2 scrollbar-hide">
                       {products.map(p => (
                         <button 
                           key={p.id}
                           type="button"
                           onClick={() => toggleProduct(p.id)}
                           className={`p-4 rounded-2xl border-2 transition-all flex items-center justify-between ${formData.productIds.includes(p.id) ? 'border-blue-600 bg-blue-50 shadow-sm' : 'border-gray-100 hover:border-gray-200'}`}
                         >
                            <div className="flex items-center gap-3">
                               <img src={p.images[0]} className="w-8 h-8 rounded-lg object-cover" alt="" />
                               <span className="text-sm font-bold text-gray-700">{p.name}</span>
                            </div>
                            {formData.productIds.includes(p.id) && <Check className="h-4 w-4 text-blue-600" />}
                         </button>
                       ))}
                    </div>
                 </div>

                 <button 
                   type="submit" 
                   className="w-full py-6 bg-blue-600 text-white rounded-[2rem] font-black text-lg shadow-2xl hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                 >
                    Launch Power Bundle <Zap className="h-5 w-5 fill-current" />
                 </button>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};

export default AdminBundles;
